import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from '../../../../../../index';
import FormsRow from '../../../../../FormsRow/FormsRow';
import FormInput from '../../../../../FormInput/FormInput';
import { Namespace } from '../../../../../../../../../translation/namespace';
import {
  getUserTopResults,
  AbstractFormsComboBox,
  ListItemPerson,
  ListBoxModifier,
  TopUserResult,
  useSingleSelectComboBox,
  ListItemEmpty,
  PersonPreview,
} from '../../../../../ComboBox';
import { StatusInfoError } from '../../../../../../../../../_lib/components/StatusInfo';
import { FormSubmitButton } from '../../../../../FormSubmitButton/FormSubmitButton';
import { LinkchooserLinkType } from '../../../../../../../../../types/api/LinkchooserLinkType';
import { StatusInfoModifier } from '../../../../../../../../../_lib/components/StatusInfo/partials/StatusInfo';

export function LinkchooserPersonTemplate() {
  const { inputs, onChange, submitActionEnabled } = useFormContext();
  const { name, required, constraints, comboBoxUrl } = inputs.q;
  const { t } = useTranslation(Namespace.Linkchooser);

  const linkType = LinkchooserLinkType.Person;

  const initialItem = inputs.q.selected && TopUserResult(inputs.q.selected);

  const { getComboBoxProps, clearSelection, selectedValue } = useSingleSelectComboBox(
    getUserTopResults,
    {
      suggestUrl: comboBoxUrl?.url || '', // should never happen as we define it in frontend
      inputValue: initialItem && initialItem.profileNameWithoutHighlighting,
    },
    initialItem
  );

  useEffect(() => {
    // https://tracker.seitenbau.net/browse/SIBZIEL-13253
    if (!selectedValue) {
      inputs.q.clear(false);
      inputs.text.clear(false);
    } else {
      onChange(name, selectedValue?.id);
      inputs.q.selected = selectedValue;
    }

    if (!inputs.text.value) {
      const text = selectedValue?.profileNameWithoutHighlighting || '';
      onChange(inputs.text.name, text);
    }
  }, [selectedValue]);

  const hasValueChanged = () => {
    return submitActionEnabled() && ((inputs.q.value && inputs.q.value !== '') || inputs.text.valueChanged());
  };

  return (
    <>
      <FormsRow>
        <AbstractFormsComboBox
          id="combobox-linkchooser"
          name={name}
          required={required}
          labelTitle={t(`${linkType}.q.label`)}
          maxLength={constraints?.maxLength}
          modifierListBox={ListBoxModifier.RELATIVE}
          {...getComboBoxProps()}
        >
          {item => (item ? <ListItemPerson user={item} /> : <ListItemEmpty />)}
        </AbstractFormsComboBox>
      </FormsRow>
      {selectedValue && selectedValue.error && (
        <StatusInfoError
          modifier={StatusInfoModifier.MARGIN_BOTTOM}
          message={t(`errors.${selectedValue.error.status}`) as string}
        />
      )}
      {selectedValue && selectedValue.pageLink && (
        <FormsRow>
          <PersonPreview
            id={selectedValue.id}
            profileLink={selectedValue.profileLink}
            phoneLink={selectedValue.phoneLink}
            emailLink={selectedValue.emailLink}
            orgUnit={selectedValue.orgUnit}
            profileImageUrl={selectedValue.profileImageUrl}
            reset={clearSelection}
          />
        </FormsRow>
      )}
      <FormsRow>
        <FormInput
          id="linkchooser-text-input"
          labelTitle={t(`${linkType}.text.label`)}
          name={inputs.text.name}
          value={inputs.text.value}
          maxLength={inputs.text.constraints?.maxLength}
          onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => onChange(inputs.text.name, target.value)}
        />
      </FormsRow>
      <FormSubmitButton buttonLabel={t(`${linkType}.submitFormButtonLabel`)} enabled={hasValueChanged()} />
    </>
  );
}
